/**
 * De rentecalculator op /wettelijke-rente-berekenen.
 *
 * Alles gebeurt in de browser: er gaat geen bedrag en geen datum naar een
 * server en er wordt niets bewaard. Het geraamte staat in
 * resources/views/sections/_rente_calculator.antlers.html en de opmaak in
 * resources/css/elements/rente-calculator.css.
 *
 * De percentages komen uit de tarieventabellen op de pagina zelf, zodat het
 * overzicht dat de lezer ziet en de som die hier gemaakt wordt dezelfde bron
 * hebben. Een nieuw halfjaartarief voeg je daar toe als rij.
 *
 * De som volgt artikel 6:119 BW: de rente loopt per dag over de hoofdsom, en na
 * elk vol jaar wordt de rente van dat jaar bij de hoofdsom geteld. Een zelf
 * afgesproken rente rekent enkelvoudig, want die bijschrijving is een regel
 * voor de wettelijke rente.
 */

const DAG = 86400000;

const geld = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

const datumtekst = new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
});

const UITLEG = {
    particulier: 'De wettelijke rente voor niet-handelstransacties. Die geldt bij een klant die geen ondernemer is.',
    handel: 'De wettelijke handelsrente. Die geldt bij een klant die ondernemer is of een overheidsorganisatie.',
    eigen: 'Een rente die je zelf met je klant hebt afgesproken, bijvoorbeeld in je algemene voorwaarden.',
};

const RENTENAAM = {
    particulier: 'Wettelijke rente',
    handel: 'Wettelijke handelsrente',
    eigen: 'Afgesproken rente',
};

const kaart = document.querySelector('[data-rentecalculator]');
const tarieventabel = document.querySelector('[data-tarieven]');

if (kaart && tarieventabel) {
    start(kaart, tarievenUit(tarieventabel));
}

function start(kaart, tarieven) {
    const regels = kaart.querySelector('[data-regels]');
    const sjabloon = document.querySelector('[data-regelsjabloon]');
    const eigenveld = kaart.querySelector('[data-eigenpercentage]');
    const soortuitleg = kaart.querySelector('[data-soortuitleg]');
    const totdatum = kaart.querySelector('[data-veld="tot"]');
    const renteLabel = kaart.querySelector('[data-label="rente"]');
    const melding = kaart.querySelector('[data-melding]');
    const kopieerstatus = kaart.querySelector('[data-kopieerstatus]');

    const uit = {
        hoofdsom: kaart.querySelector('[data-uit="hoofdsom"]'),
        rente: kaart.querySelector('[data-uit="rente"]'),
        totaal: kaart.querySelector('[data-uit="totaal"]'),
    };

    let teller = 1;
    let laatste = null;

    vulBeginwaarden();

    kaart.addEventListener('input', teken);
    kaart.addEventListener('change', teken);

    kaart.addEventListener('click', (gebeurtenis) => {
        const knop = gebeurtenis.target.closest('[data-actie]');

        if (! knop) {
            return;
        }

        if (knop.dataset.actie === 'voegtoe') {
            voegRegelToe();
        }

        if (knop.dataset.actie === 'verwijder') {
            verwijderRegel(knop.closest('[data-regel]'));
        }

        if (knop.dataset.actie === 'kopieer') {
            kopieer(laatste, kopieerstatus);
        }
    });

    teken();

    /**
     * De einddatum is vandaag en de eerste factuur staat drie maanden open, zodat
     * de pagina meteen een uitkomst laat zien. De datums worden hier gezet en niet
     * in de template, omdat de HTML van deze pagina statisch gecachet wordt en
     * vandaag dan de dag van het cachen zou zijn.
     */
    function vulBeginwaarden() {
        const vandaag = Date.UTC(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate()
        );

        totdatum.value = alsWaarde(vandaag);

        const eerste = regels.querySelector('[data-veld="vanaf"]');

        if (eerste && eerste.value === '') {
            eerste.value = alsWaarde(vandaag - 90 * DAG);
        }
    }

    function voegRegelToe() {
        teller += 1;

        const regel = sjabloon.content.cloneNode(true);

        regel.querySelectorAll('[id], [for]').forEach((element) => {
            if (element.id) {
                element.id = element.id.replace('__n__', String(teller));
            }

            if (element.htmlFor) {
                element.htmlFor = element.htmlFor.replace('__n__', String(teller));
            }
        });

        regels.appendChild(regel);
        regels.lastElementChild.querySelector('[data-veld="bedrag"]').focus();

        teken();
    }

    function verwijderRegel(regel) {
        if (! regel || regels.querySelectorAll('[data-regel]').length < 2) {
            return;
        }

        regel.remove();
        teken();
    }

    function teken() {
        const soort = gekozenSoort(kaart);
        const schaal = schaalVoor(soort, tarieven, leesGetal(kaart.querySelector('[data-veld="eigen"]').value));
        const samengesteld = soort !== 'eigen';

        eigenveld.hidden = soort !== 'eigen';
        soortuitleg.textContent = UITLEG[soort];
        renteLabel.textContent = RENTENAAM[soort];

        const tot = leesDatum(totdatum.value);
        const problemen = [];
        const uitkomsten = [];

        if (tot === null) {
            problemen.push('Vul in tot welke datum je de rente wilt berekenen.');
        }

        if (schaal.length === 0) {
            problemen.push('Vul het afgesproken rentepercentage in.');
        }

        regels.querySelectorAll('[data-regel]').forEach((regel, index) => {
            const uitkomst = berekenRegel(regel, tot, schaal, samengesteld, problemen, index + 1);

            toonRegel(regel, uitkomst);

            if (uitkomst) {
                uitkomsten.push(uitkomst);
            }
        });

        regels.querySelectorAll('[data-actie="verwijder"]').forEach((knop) => {
            knop.hidden = regels.querySelectorAll('[data-regel]').length < 2;
        });

        melding.textContent = problemen[0] ?? '';

        if (uitkomsten.length === 0) {
            uit.hoofdsom.textContent = uit.rente.textContent = uit.totaal.textContent = '—';
            laatste = null;

            return;
        }

        const hoofdsom = rond(uitkomsten.reduce((totaal, regel) => totaal + regel.hoofdsom, 0));
        const rente = rond(uitkomsten.reduce((totaal, regel) => totaal + regel.rente, 0));

        laatste = { hoofdsom, rente, totaal: rond(hoofdsom + rente), soort, regels: uitkomsten };

        uit.hoofdsom.textContent = geld.format(laatste.hoofdsom);
        uit.rente.textContent = geld.format(laatste.rente);
        uit.totaal.textContent = geld.format(laatste.totaal);
    }

    /**
     * Rekent één factuurregel door en houdt bij waarom een regel geen uitkomst
     * heeft, zodat de gebruiker onder het formulier leest wat er nog mist.
     *
     * @return {?{hoofdsom: number, rente: number, dagen: number, vanaf: number, tot: number, perioden: Array}}
     */
    function berekenRegel(regel, tot, schaal, samengesteld, problemen, nummer) {
        const hoofdsom = leesGetal(regel.querySelector('[data-veld="bedrag"]').value);
        const vanaf = leesDatum(regel.querySelector('[data-veld="vanaf"]').value);

        if (hoofdsom === null || vanaf === null || tot === null || schaal.length === 0) {
            if (hoofdsom !== null && vanaf === null) {
                problemen.push('Vul bij factuur ' + nummer + ' in vanaf welke dag de rente loopt.');
            }

            return null;
        }

        if (vanaf < schaal[0].vanaf) {
            problemen.push(
                'De calculator kent de percentages vanaf '
                + datumtekst.format(new Date(schaal[0].vanaf))
                + '. Voor factuur ' + nummer + ' ligt de begindatum daarvoor.'
            );

            return null;
        }

        if (vanaf >= tot) {
            problemen.push('Bij factuur ' + nummer + ' ligt de begindatum niet vóór de einddatum.');

            return null;
        }

        const som = berekenRente(hoofdsom, vanaf, tot, schaal, samengesteld);

        return {
            hoofdsom,
            vanaf,
            tot,
            rente: rond(som.rente),
            dagen: som.dagen,
            perioden: som.perioden,
        };
    }
}

/**
 * Leest de tarieven uit de tabellen op de pagina. Elke rij draagt de soort, de
 * ingangsdatum en het percentage, dus het zichtbare overzicht en de berekening
 * kunnen niet uit elkaar lopen.
 *
 * @return {Object<string, Array<{vanaf: number, percentage: number}>>}
 */
function tarievenUit(wortel) {
    const tarieven = {};

    wortel.querySelectorAll('[data-soort][data-vanaf][data-percentage]').forEach((rij) => {
        const soort = rij.dataset.soort;
        const vanaf = leesDatum(rij.dataset.vanaf);
        const percentage = Number.parseFloat(rij.dataset.percentage);

        if (vanaf === null || ! Number.isFinite(percentage)) {
            return;
        }

        tarieven[soort] = tarieven[soort] ?? [];
        tarieven[soort].push({ vanaf, percentage });
    });

    Object.values(tarieven).forEach((reeks) => reeks.sort((een, twee) => een.vanaf - twee.vanaf));

    return tarieven;
}

/**
 * De reeks percentages waarmee gerekend wordt. Bij een eigen afspraak is dat er
 * één, die vanaf het begin van de tijd geldt.
 */
function schaalVoor(soort, tarieven, eigen) {
    if (soort !== 'eigen') {
        return tarieven[soort] ?? [];
    }

    return eigen === null ? [] : [{ vanaf: -Infinity, percentage: eigen }];
}

/**
 * De rente over één hoofdsom, geknipt bij elke tariefwijziging en bij elke
 * verjaardag van de begindatum. Na een vol jaar gaat de rente van dat jaar bij
 * de hoofdsom, zoals artikel 6:119 lid 2 BW voorschrijft.
 *
 * @return {{rente: number, dagen: number, perioden: Array}}
 */
function berekenRente(hoofdsom, vanaf, tot, schaal, samengesteld) {
    const perioden = [];

    let saldo = hoofdsom;
    let rente = 0;
    let jaarVanaf = vanaf;

    while (jaarVanaf < tot) {
        const jaarTot = jaarLater(jaarVanaf);
        const knip = Math.min(jaarTot, tot);
        const dagenInJaar = (jaarTot - jaarVanaf) / DAG;

        let jaarRente = 0;

        for (const stuk of stukken(jaarVanaf, knip, schaal)) {
            const dagen = (stuk.tot - stuk.van) / DAG;
            const bedrag = saldo * (stuk.percentage / 100) * (dagen / dagenInJaar);

            jaarRente += bedrag;

            perioden.push({
                van: stuk.van,
                tot: stuk.tot,
                percentage: stuk.percentage,
                dagen,
                saldo,
                rente: bedrag,
            });
        }

        rente += jaarRente;

        if (samengesteld && jaarTot <= tot) {
            saldo += jaarRente;
        }

        jaarVanaf = jaarTot;
    }

    return { rente, dagen: (tot - vanaf) / DAG, perioden };
}

/**
 * Knipt een periode in stukken die elk onder één percentage vallen.
 *
 * @return {Array<{van: number, tot: number, percentage: number}>}
 */
function stukken(van, tot, schaal) {
    const delen = [];

    let cursor = van;

    while (cursor < tot) {
        const plek = schaal.findLastIndex((tarief) => tarief.vanaf <= cursor);

        if (plek === -1) {
            return delen;
        }

        const volgende = schaal[plek + 1]?.vanaf ?? Infinity;
        const grens = Math.min(volgende, tot);

        delen.push({ van: cursor, tot: grens, percentage: schaal[plek].percentage });

        cursor = grens;
    }

    return delen;
}

function toonRegel(regel, uitkomst) {
    const plek = regel.querySelector('[data-regeluitkomst]');

    if (! uitkomst) {
        plek.textContent = '';

        return;
    }

    const kop = document.createElement('p');
    kop.className = 'rente-regel__rente';
    kop.textContent = geld.format(uitkomst.rente) + ' rente over ' + uitkomst.dagen + ' dagen';

    /* De uitkomst wordt bij elke toetsaanslag opnieuw getekend. Stond de
       berekening open, dan blijft hij open. */
    const openstaand = plek.querySelector('.rente-berekening')?.open ?? false;
    const berekening = uitklapper(uitkomst);

    berekening.open = openstaand;

    plek.replaceChildren(kop, berekening);
}

/**
 * De berekening per periode, zodat je kunt narekenen wat de calculator deed bij
 * een tariefwijziging en bij de jaarlijkse bijschrijving.
 */
function uitklapper(uitkomst) {
    const details = document.createElement('details');
    details.className = 'rente-berekening';

    const samenvatting = document.createElement('summary');
    samenvatting.textContent = 'Bekijk de berekening';
    details.appendChild(samenvatting);

    const lijst = document.createElement('ul');
    lijst.className = 'rente-berekening__lijst';

    uitkomst.perioden.forEach((periode) => {
        const item = document.createElement('li');

        item.textContent = datumtekst.format(new Date(periode.van))
            + ' tot ' + datumtekst.format(new Date(periode.tot))
            + ': ' + geld.format(periode.saldo) + ' × ' + procent(periode.percentage)
            + ' × ' + periode.dagen + ' dagen = ' + geld.format(rond(periode.rente));

        lijst.appendChild(item);
    });

    details.appendChild(lijst);

    return details;
}

function kopieer(uitkomst, status) {
    if (! uitkomst) {
        melden(status, 'Vul eerst een bedrag en een datum in.');

        return;
    }

    const regels = uitkomst.regels.map((regel) => geld.format(regel.hoofdsom)
        + ' open sinds ' + datumtekst.format(new Date(regel.vanaf))
        + ': ' + geld.format(regel.rente) + ' rente over ' + regel.dagen + ' dagen');

    const tekst = regels.join('\n')
        + '\nHoofdsom: ' + geld.format(uitkomst.hoofdsom)
        + '\nRente: ' + geld.format(uitkomst.rente)
        + '\nTotaal te vorderen: ' + geld.format(uitkomst.totaal);

    /*
     * De clipboard-API bestaat alleen op een beveiligde verbinding en mag door de
     * browser geweigerd worden. Lukt het niet, dan zegt de tool dat, in plaats van
     * te doen alsof er iets op het klembord staat.
     */
    if (! navigator.clipboard?.writeText) {
        melden(status, 'Kopiëren lukt niet in deze browser. Selecteer de bedragen zelf.');

        return;
    }

    navigator.clipboard.writeText(tekst).then(
        () => melden(status, 'Gekopieerd.'),
        () => melden(status, 'Kopiëren lukte niet. Selecteer de bedragen zelf.')
    );
}

function melden(status, tekst) {
    status.textContent = tekst;

    window.setTimeout(() => {
        status.textContent = '';
    }, 4000);
}

function gekozenSoort(kaart) {
    const keuze = kaart.querySelector('input[name="rentesoort"]:checked');

    return keuze ? keuze.value : 'particulier';
}

/**
 * Nederlandse notatie: de komma scheidt de centen, de punt de duizendtallen.
 * Staat er alleen een punt, dan hangt het van het aantal punten af wat de
 * schrijver bedoelde; bij één punt leest de browser hem als decimaalteken,
 * zoals een geplakt bedrag uit een spreadsheet.
 */
function leesGetal(waarde) {
    let ruw = String(waarde).trim().replace(/[\s€%]/g, '');

    if (ruw === '') {
        return null;
    }

    if (ruw.includes(',')) {
        ruw = ruw.replace(/\./g, '').replace(',', '.');
    } else if ((ruw.match(/\./g) || []).length > 1) {
        ruw = ruw.replace(/\./g, '');
    }

    const getal = Number.parseFloat(ruw);

    return Number.isFinite(getal) && getal >= 0 ? getal : null;
}

/**
 * Een datumveld levert jjjj-mm-dd. De datum wordt in UTC gelezen, zodat een
 * zomertijdgrens geen halve dag in de uitkomst schuift.
 */
function leesDatum(waarde) {
    const delen = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(waarde).trim());

    if (! delen) {
        return null;
    }

    return Date.UTC(Number(delen[1]), Number(delen[2]) - 1, Number(delen[3]));
}

function alsWaarde(tijdstip) {
    return new Date(tijdstip).toISOString().slice(0, 10);
}

/**
 * Dezelfde dag een jaar later. Valt de begindatum op 29 februari, dan komt de
 * verjaardag in een gewoon jaar op 1 maart te liggen.
 */
function jaarLater(tijdstip) {
    const datum = new Date(tijdstip);

    return Date.UTC(datum.getUTCFullYear() + 1, datum.getUTCMonth(), datum.getUTCDate());
}

function procent(getal) {
    return String(getal).replace('.', ',') + '%';
}

function rond(getal) {
    return Math.round((getal + Number.EPSILON) * 100) / 100;
}
